import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { mapCreatorsFromApiModelToViewModel } from '~/clientApi/clientApi.mapper';
import {
  AGENCIES_COLLECTION_QUERY_KEY,
  CREATORS_COLLECTION_QUERY_KEY,
} from '~/queries/keys';
import { selectTalentManager } from '~/selectors/talent-manager';

import { getCreators } from './usePaginatedCreators.api';
import { PAGE_SIZE } from './usePaginatedCreators.constants';

export interface IUseCreatorsProps {
  enableAgencyRoster?: boolean;
  isEnabled?: boolean;
  isSearch?: boolean;
  pageSize?: number;
}

export interface IUseCreatorsParams {
  keyword?: string;
}
export type UsePaginatedCreatorsReturn = ReturnType<
  typeof usePaginatedCreators
>;

export function usePaginatedCreators({
  enableAgencyRoster,
  isEnabled = true,
  isSearch = false,
  pageSize = PAGE_SIZE,
}: IUseCreatorsProps = {}) {
  const [filters, setFilters] = useState<IUseCreatorsParams>({
    keyword: '',
  });
  const manager = useSelector(selectTalentManager);
  const agencyId: string | undefined = enableAgencyRoster
    ? manager?.agency?.id
    : undefined;

  const queryKey = [
    agencyId ? AGENCIES_COLLECTION_QUERY_KEY : CREATORS_COLLECTION_QUERY_KEY,
    { managerId: manager?.id, agencyId, enableAgencyRoster, ...filters },
  ];

  const { data, ...rest } = useInfiniteQuery({
    queryKey: queryKey,
    queryFn: ({ pageParam }) => {
      return getCreators({
        agencyId,
        pageSize,
        page: pageParam,
        keyword: filters.keyword,
      }).then(({ results, totalItems }) => {
        return {
          results: mapCreatorsFromApiModelToViewModel(results),
          totalItems,
        };
      });
    },
    getNextPageParam: (page, pages) => {
      return Math.floor(pages.length * pageSize) <= (page.totalItems ?? 0)
        ? pages.length + 1
        : undefined;
    },
    staleTime: 5 * 60 * 1000,
    enabled: (isSearch && !!filters.keyword?.length) || isEnabled,
  });

  const updatedFilters = (updatedFilters: Partial<IUseCreatorsParams>) => {
    setFilters({ ...filters, ...updatedFilters });
  };

  return {
    ...rest,
    creators:
      data?.pages.reduce((acc, { results }) => [...acc, ...results], []) ?? [],
    filters,
    setFilters: updatedFilters,
  };
}
